import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Mail, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [debugCode, setDebugCode] = useState(null);
  const [emailConfigured, setEmailConfigured] = useState(true);

  const submit = async () => {
    if (!email.trim() || !email.includes("@")) {
      toast.error("Enter a valid email"); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/api/auth/forgot-password", { email: email.trim() });
      setSent(true);
      setDebugCode(data.debug_code || null);
      setEmailConfigured(!!data.email_configured);
      toast.success("If that email is registered, a code is on the way.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally { setBusy(false); }
  };

  const proceed = () => {
    nav("/reset-password", { state: { email: email.trim() } });
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="forgot-password-page">
      <Header />
      <main id="main-content" className="flex-1 max-w-[500px] w-full mx-auto px-4 sm:px-6 py-12">
        <div className="card-surface p-6 sm:p-7 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none opacity-40"
               style={{ background: "radial-gradient(circle at 100% 0%, rgba(0,255,156,0.14), transparent 60%)" }} />
          <div className="relative">
            <div className="label-mono">Password reset</div>
            <h1 className="text-3xl text-white font-semibold tracking-tight mt-1">
              Forgot your password?
            </h1>
            <p className="text-white/60 text-[13px] mt-2 leading-relaxed">
              Enter the email on your FlowFinder account and we'll send you a 6-digit code.
              The code expires in 5 minutes.
            </p>

            {!sent ? (
              <div className="mt-5 space-y-3">
                <div>
                  <label className="label-mono">Email</label>
                  <div className="relative mt-1">
                    <Mail className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && submit()}
                      placeholder="you@example.com"
                      autoComplete="email"
                      autoFocus
                      className="input-glass w-full pl-8"
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>
                <button
                  onClick={submit}
                  disabled={busy || !email.trim()}
                  className="btn-accent w-full inline-flex items-center justify-center gap-2"
                  data-testid="forgot-send-btn"
                >
                  {busy ? "Sending…" : "Send code"} <ArrowRight className="w-3.5 h-3.5" />
                </button>
                <div className="text-center label-mono">
                  <Link to="/login" className="hover:text-white transition-colors">
                    ← Back to sign in
                  </Link>
                </div>
              </div>
            ) : (
              <div className="mt-5 space-y-3" data-testid="forgot-sent-state">
                <div className="rounded-2xl border border-[var(--bull)]/30 bg-[var(--bull)]/[0.06] p-3 flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-[var(--bull)] shrink-0" />
                  <div className="text-[13px] text-white/85 leading-relaxed">
                    If <span className="font-mono">{email}</span> is on file, you'll receive a 6-digit code
                    within a minute. Check your spam folder if you don't see it.
                  </div>
                </div>
                {debugCode && !emailConfigured && (
                  <div className="rounded-xl border border-[#ffd166]/40 bg-[#ffd166]/[0.06] p-3" data-testid="debug-code-block">
                    <div className="label-mono text-[#ffd166]">Dev mode</div>
                    <div className="text-white/85 text-[13px] mt-1 leading-relaxed">
                      Email delivery isn't configured yet. Your code is:
                    </div>
                    <div className="font-mono text-2xl tracking-widest text-[#ffd166] mt-2" data-testid="debug-code-value">
                      {debugCode}
                    </div>
                    <div className="text-white/50 text-[11px] mt-2">
                      Add RESEND_API_KEY or SMTP_* env vars to backend/.env to activate email delivery.
                    </div>
                  </div>
                )}
                <button
                  onClick={proceed}
                  className="btn-accent w-full inline-flex items-center justify-center gap-2"
                  data-testid="forgot-continue-btn"
                >
                  Enter code <ArrowRight className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setSent(false)}
                  className="btn-ghost w-full text-[12px]"
                  data-testid="forgot-try-again-btn"
                >
                  Use a different email
                </button>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
